import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';

// Platform Components
import LandingPage from './components/platform/LandingPage';
import PlatformDashboard from './components/platform/PlatformDashboard';
import BookDemo from './components/platform/BookDemo';

// Unified Login
import UnifiedLogin from './components/auth/UnifiedLogin';
import OrganizationSignup from './components/signup/OrganizationSignup';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import Dashboard from './components/dashboard/ModularDashboard';
import Layout from './components/layout/Layout';
import Inventory from './components/inventory/ModularInventory';
import Customers from './components/customers/Customers';
import ModularReservations from './components/reservations/ModularReservations';
import Reports from './components/reports/ModularReports';
import Timesheets from './components/timesheets/Timesheets';
import Employees from './components/employees/Employees';
import EmployeeDetails from './components/employees/EmployeeDetails';
import EmployeeSignup from './components/auth/EmployeeSignup';
import LoadingSpinner from './components/common/LoadingSpinner';

// Storefront Components
import Storefront from './components/storefront/Storefront';
import ContactInformation from './components/storefront/ContactInformation';
import SalesChannels from './components/storefront/SalesChannels';
import OpeningHours from './components/storefront/OpeningHours';
import Emails from './components/storefront/Emails';
import BusinessDetails from './components/storefront/BusinessDetails';
import General from './components/storefront/General';
import ItemReservations from './components/storefront/ItemReservations';
import BrandingAppearance from './components/storefront/BrandingAppearance';
import Payments from './components/storefront/Payments';
import TermsPolicies from './components/storefront/TermsPolicies';
import Taxes from './components/storefront/Taxes';
import UsersPermissions from './components/storefront/UsersPermissions';
import NotificationPreferences from './components/storefront/NotificationPreferences';
import Account from './components/storefront/Account';

// Public Components
import ServiceBooking from './components/public/ServiceBooking';
import BookingView from './components/public/BookingView';
import CustomerPreFill from './components/public/CustomerPreFill';

// Detect if this is a subdomain or main domain
const useSubdomain = () => {
  const hostname = window.location.hostname;

  // Skip subdomain detection for staging environments - use path-based routing instead
  const isStaging = hostname.includes('condor-staging.vercel.app') ||
                    hostname.includes('vercel.app') ||
                    process.env.NODE_ENV === 'staging' ||
                    process.env.REACT_APP_ENVIRONMENT === 'staging';

  if (isStaging) {
    // In staging, we don't use subdomains since Vercel doesn't support dynamic subdomains
    return null;
  }

  // Railway production domains should be treated as business apps (not platform)
  // This allows condor-production.up.railway.app to work as a business app
  if (hostname.includes('railway.app')) {
    // For Railway, we'll use a default subdomain or fetch from user context
    // Return a special marker that indicates we should load business app
    return 'railway-business';
  }

  // In development, check for patterns like demo.localhost or golf-carts.localhost
  if (hostname.includes('localhost')) {
    const parts = hostname.split('.');
    return parts.length > 1 && parts[0] !== 'www' ? parts[0] : null;
  }

  // Check for condrs.com subdomains (e.g., demo.condrs.com, golf-carts.condrs.com)
  if (hostname.endsWith('.condrs.com')) {
    const parts = hostname.split('.');
    // Should be format: subdomain.condrs.com
    if (parts.length === 3 && parts[0] !== 'www' && parts[0] !== 'api') {
      return parts[0];
    }
  }

  return null;
};

// Platform App - Landing page and unified login
const PlatformApp = () => {
  const { isAuthenticated, isSuperAdmin, user } = useAuth();
  const location = useLocation();

  console.log('PlatformApp - isAuthenticated:', isAuthenticated, 'isSuperAdmin:', isSuperAdmin, 'user role:', user?.role, 'pathname:', location.pathname);

  // Allow unauthenticated access to signup and other public routes
  const publicRoutes = ['/signup', '/login', '/setup-account', '/book-demo', '/demo', '/contact', '/features', '/pricing', '/forgot-password', '/reset-password', '/register'];
  const isPublicRoute = publicRoutes.some(route => location.pathname.startsWith(route)) ||
                        location.pathname.startsWith('/service/') ||
                        location.pathname.match(/^\/[^/]+\/register$/);

  if (!isAuthenticated && !isPublicRoute) {
    return <UnifiedLogin />;
  }

  // If authenticated and super admin, show admin dashboard
  if (isSuperAdmin) {
    return (
      <Routes>
        <Route path="/" element={<Navigate to="/platform/dashboard" replace />} />
        <Route path="/platform/dashboard" element={<PlatformDashboard />} />
        <Route path="/admin" element={<Navigate to="/platform/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/platform/dashboard" replace />} />
      </Routes>
    );
  }

  // If authenticated business admin or employee, redirect to their organization subdomain
  if (isAuthenticated && user?.role && ['BUSINESS_ADMIN', 'EMPLOYEE'].includes(user.role) && user.organization?.subdomain) {
    // Check if we're already on the correct subdomain to avoid redirect loops
    const currentHostname = window.location.hostname;
    const expectedSubdomain = `${user.organization.subdomain}.condor.localhost`;
    const expectedProductionSubdomain = `${user.organization.subdomain}.condrs.com`;

    // Skip subdomain redirects for staging environment - Vercel doesn't support dynamic subdomains
    const isStaging = currentHostname.includes('condor-staging.vercel.app') ||
                      currentHostname.includes('vercel.app') ||
                      process.env.NODE_ENV === 'staging' ||
                      process.env.REACT_APP_ENVIRONMENT === 'staging';

    // Skip subdomain redirects for Railway - Railway domains work as business apps directly
    const isRailway = currentHostname.includes('railway.app');

    console.log('🔍 REDIRECT DEBUG:', {
      currentHostname,
      isStaging,
      isRailway,
      NODE_ENV: process.env.NODE_ENV,
      REACT_APP_ENVIRONMENT: process.env.REACT_APP_ENVIRONMENT,
      userRole: user?.role,
      userOrganization: user?.organization?.subdomain,
      expectedSubdomain,
      expectedProductionSubdomain,
      willRedirect: !isStaging && !isRailway && currentHostname !== expectedSubdomain && currentHostname !== expectedProductionSubdomain
    });

    // Only redirect if NOT in staging, NOT on Railway, AND not already on correct subdomain
    if (!isStaging && !isRailway && currentHostname !== expectedSubdomain && currentHostname !== expectedProductionSubdomain) {
      // Redirect to subdomain URL instead of path-based routing
      const isProduction = currentHostname.includes('condrs.com');
      const protocol = isProduction ? 'https' : 'http';
      const domain = isProduction ? 'condrs.com' : 'condor.localhost:3000';
      const subdomainUrl = `${protocol}://${user.organization.subdomain}.${domain}/dashboard`;

      // Redirect to subdomain
      window.location.href = subdomainUrl;

      // Return loading spinner while redirecting
      return <LoadingSpinner />;
    }

    // For staging, render the business app directly without subdomain redirect
    if (isStaging) {
      console.log('🚀 STAGING MODE: Rendering business app without subdomain redirect');
      return <BusinessApp subdomain={user.organization.subdomain} />;
    }
  }

  // For non-super-admin users on main domain, show landing page
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<UnifiedLogin />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/setup-account/:token" element={<EmployeeSignup />} />
      <Route path="/setup-account" element={<EmployeeSignup />} />
      <Route path="/signup" element={<OrganizationSignup />} />
      <Route path="/book-demo" element={<BookDemo />} />
      <Route path="/demo" element={<div>Demo Page (TODO)</div>} />
      <Route path="/contact" element={<div>Contact Page (TODO)</div>} />
      <Route path="/features" element={<div>Features Page (TODO)</div>} />
      <Route path="/pricing" element={<div>Pricing Page (TODO)</div>} />
      <Route path="/service/:token" element={<ServiceBooking />} />
      <Route path="/booking/view/:viewToken" element={<BookingView />} />
      <Route path="/register" element={<CustomerPreFill />} />
      <Route path="/:slug/register" element={<CustomerPreFill />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

// Business App - Equipment rental management system (or other business apps)
const BusinessApp = ({ subdomain }) => {
  const { isAuthenticated, loading } = useAuth();

  console.log(`BusinessApp render for ${subdomain} - isAuthenticated:`, isAuthenticated, 'loading:', loading);

  if (loading) {
    console.log('BusinessApp: Showing loading spinner');
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    console.log('BusinessApp: Showing unified login component');
    return (
      <Routes>
        <Route path="/setup-account/:token" element={<EmployeeSignup />} />
        <Route path="/setup-account" element={<EmployeeSignup />} />
        <Route path="/service/:token" element={<ServiceBooking />} />
        <Route path="/booking/view/:viewToken" element={<BookingView />} />
        <Route path="/register" element={<CustomerPreFill />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="*" element={<UnifiedLogin />} />
      </Routes>
    );
  }

  console.log('BusinessApp: Rendering authenticated layout with routes');

  return (
    <Routes>
      <Route path="/" element={<Navigate to="dashboard" replace />} />
      <Route path="/" element={<Layout />}>
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="inventory" element={<Inventory />} />
        <Route path="customers" element={<Customers />} />
        <Route path="reservations" element={<ModularReservations />} />
        <Route path="timesheets" element={<Timesheets />} />
        <Route path="employees" element={<Employees />} />
        <Route path="employees/:id" element={<EmployeeDetails />} />
        <Route path="reports" element={<Reports />} />
        
        {/* Storefront Management Routes */}
        <Route path="storefront" element={<Storefront />}>
          <Route path="contact" element={<ContactInformation />} />
          <Route path="sales-channels" element={<SalesChannels />} />
          <Route path="hours" element={<OpeningHours />} />
          <Route path="emails" element={<Emails />} />
          <Route path="business-details" element={<BusinessDetails />} />
          <Route path="general" element={<General />} />
          <Route path="item-reservations" element={<ItemReservations />} />
          <Route path="branding" element={<BrandingAppearance />} />
          <Route path="payments" element={<Payments />} />
          <Route path="terms" element={<TermsPolicies />} />
          <Route path="taxes" element={<Taxes />} />
          <Route path="permissions" element={<UsersPermissions />} />
          <Route path="notifications" element={<NotificationPreferences />} />
          <Route path="account" element={<Account />} />
        </Route>
        
        {/* Public routes that should work even when authenticated */}
        <Route path="/booking/view/:viewToken" element={<BookingView />} />
        
        <Route path="*" element={<Navigate to="dashboard" replace />} />
      </Route>
    </Routes>
  );
};

function App() {
  const subdomain = useSubdomain();
  const location = useLocation();

  console.log('App render - subdomain:', subdomain, 'pathname:', location.pathname);

  // If there's a subdomain, render the business app
  if (subdomain) {
    return <BusinessApp subdomain={subdomain} />;
  }

  // Otherwise, render the platform app
  return <PlatformApp />;
}

export default App;